import { useState, useMemo, useEffect } from 'react';
import { Check, ChevronsUpDown, Loader2, Tag, Package, Box, X, Plus, ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  useShopifyTagSearch,
  useShopifyProductTypeSearch,
  useShopifyProductSearch,
  useShopifyVariantSearch,
} from '../hooks/useLineItemExclusions';
import type { MatchField } from '@threadheads/shared';

export interface PickerSelection {
  value: string;
  display_label: string;
}

interface Props {
  matchField: MatchField;
  selections: PickerSelection[];
  onChange: (selections: PickerSelection[]) => void;
  /** Optional cap on how many items can be picked at once (soft UX guard) */
  maxSelections?: number;
}

interface PickerOption {
  value: string;
  display_label: string;
  productId?: string;
  productTitle?: string;
  secondary?: string;
  imageUrl?: string | null;
}

function useDebounced(value: string, delayMs = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const iconFor = (field: MatchField) => {
  switch (field) {
    case 'shopify_product_tag':
      return Tag;
    case 'shopify_product_type':
      return Package;
    case 'shopify_product_id':
    case 'shopify_variant_id':
      return Box;
  }
};

/**
 * Multi-select Shopify-backed picker. Behavior varies by match_field:
 *   - shopify_product_tag   : typeahead over distinct product tags; free-text
 *                             "Create {value}" option when no match
 *   - shopify_product_type  : same shape as tag
 *   - shopify_product_id    : typeahead over products with thumbnails
 *   - shopify_variant_id    : typeahead via the dedicated productVariants
 *                             endpoint (supports SKU search). Grouped by
 *                             parent product so the product image only
 *                             renders once per group.
 */
export function ShopifyMultiSelectPicker({
  matchField,
  selections,
  onChange,
  maxSelections = 50,
}: Props) {
  const { t } = useTranslation('settings');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 250);
  const Icon = iconFor(matchField);

  const isTag = matchField === 'shopify_product_tag';
  const isType = matchField === 'shopify_product_type';
  const isProduct = matchField === 'shopify_product_id';
  const isVariant = matchField === 'shopify_variant_id';

  const tagQuery = useShopifyTagSearch(debouncedSearch, open && isTag && debouncedSearch.length >= 2);
  const typeQuery = useShopifyProductTypeSearch(
    debouncedSearch,
    open && isType && debouncedSearch.length >= 2,
  );
  const productQuery = useShopifyProductSearch(debouncedSearch, open && isProduct);
  const variantQuery = useShopifyVariantSearch(
    debouncedSearch,
    open && isVariant && debouncedSearch.length >= 2,
  );

  const isLoading = isTag
    ? tagQuery.isFetching
    : isType
      ? typeQuery.isFetching
      : isProduct
        ? productQuery.isFetching
        : variantQuery.isFetching;

  const options: PickerOption[] = useMemo(() => {
    if (isTag) {
      return (tagQuery.data ?? []).map((x) => ({ value: x, display_label: x }));
    }
    if (isType) {
      return (typeQuery.data ?? []).map((x) => ({ value: x, display_label: x }));
    }
    if (isProduct) {
      return (productQuery.data ?? []).map((p) => ({
        value: p.shopifyProductId,
        display_label: p.title,
        secondary: p.productType ?? undefined,
        imageUrl: p.imageUrl ?? null,
      }));
    }
    if (isVariant) {
      return (variantQuery.data ?? []).map((v) => ({
        value: v.shopifyVariantId,
        productId: v.shopifyProductId,
        productTitle: v.productTitle,
        display_label: `${v.productTitle}${v.variantTitle ? `: ${v.variantTitle}` : ''}`,
        secondary: v.sku,
        imageUrl: v.imageUrl,
      }));
    }
    return [];
  }, [isTag, isType, isProduct, isVariant, tagQuery.data, typeQuery.data, productQuery.data, variantQuery.data]);

  // Group variant options by product identity so the parent thumbnail only
  // appears once per product (prevents 12 variants of one shirt from showing
  // the same image 12 times).
  const variantGroups = useMemo(() => {
    if (!isVariant) return null;
    const groups = new Map<string, { title: string; imageUrl: string | null | undefined; rows: PickerOption[] }>();
    for (const opt of options) {
      const productKey = opt.productId ?? opt.value;
      const existing = groups.get(productKey);
      if (existing) {
        existing.rows.push(opt);
      } else {
        groups.set(productKey, { title: opt.productTitle ?? opt.display_label, imageUrl: opt.imageUrl, rows: [opt] });
      }
    }
    return Array.from(groups.entries());
  }, [isVariant, options]);

  const selectedValues = new Set(selections.map((s) => s.value));
  const atMax = selections.length >= maxSelections;

  const togglePick = (opt: { value: string; display_label: string }, meta?: { wasCreate?: boolean }) => {
    if (selectedValues.has(opt.value)) {
      onChange(selections.filter((s) => s.value !== opt.value));
    } else if (!atMax) {
      onChange([...selections, { value: opt.value, display_label: opt.display_label }]);
      // After picking a synthetic "Create" option, clear the search so the
      // picker is ready for the next pick and the debounced value can't
      // cause a stale-selection gotcha.
      if (meta?.wasCreate) setSearch('');
    }
  };

  const removeSelection = (value: string) => {
    onChange(selections.filter((s) => s.value !== value));
  };

  const placeholder = placeholderFor(matchField, t);
  const searchPlaceholder = searchPlaceholderFor(matchField, t);
  const showTypeMorePrompt =
    open &&
    (isTag || isType || isVariant) &&
    debouncedSearch.length < 2;

  // Free-text create is only offered when:
  // - field type supports it (tags or product types only)
  // - the user has typed 2+ chars
  // - the Shopify call has SETTLED (not still loading — otherwise we'd
  //   race with real results that match the typed string)
  // - no existing option matches case-insensitively
  // - the live value (not debounced) is what we offer, to prevent stale
  //   selections if the user types quickly and clicks
  const liveNormalized = search.trim();
  const canCreate =
    (isTag || isType) &&
    !isLoading &&
    liveNormalized.length >= 2 &&
    !options.some((o) => o.value.toLowerCase() === liveNormalized.toLowerCase());

  const iconInTrigger = selections.length === 0;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="flex items-center gap-2">
              {iconInTrigger ? (
                <Icon className="h-4 w-4 text-muted-foreground" />
              ) : null}
              <span className={cn(iconInTrigger && 'text-muted-foreground')}>
                {selections.length > 0
                  ? t('exclusions.picker.selectedCount', {
                      count: selections.length,
                      defaultValue: `${selections.length} selected`,
                    })
                  : placeholder}
              </span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[320px]">
              {showTypeMorePrompt ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {searchPlaceholder}
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('exclusions.picker.loading')}
                </div>
              ) : (
                <>
                  {canCreate ? (
                    <CommandGroup heading={t('exclusions.picker.createHeading', { defaultValue: 'Create new' })}>
                      <CommandItem
                        value={`__create__${liveNormalized}`}
                        onSelect={() =>
                          togglePick(
                            { value: liveNormalized, display_label: liveNormalized },
                            { wasCreate: true },
                          )
                        }
                        className="flex items-center gap-2"
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary">
                          <Plus className="h-3.5 w-3.5" />
                        </span>
                        <span className="truncate text-sm">
                          {t('exclusions.picker.create', {
                            value: liveNormalized,
                            kind: isTag ? 'tag' : 'product type',
                            defaultValue: `Add "${liveNormalized}" as a new ${isTag ? 'tag' : 'product type'}`,
                          })}
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  ) : null}

                  {options.length === 0 && !canCreate ? (
                    <CommandEmpty>{t('exclusions.picker.empty')}</CommandEmpty>
                  ) : null}

                  {isVariant && variantGroups ? (
                    variantGroups.map(([productId, group]) => (
                      <CommandGroup key={productId} heading={group.title}>
                        {group.rows.map((opt) => renderOption(opt, {
                          showThumb: false,
                          isSelected: selectedValues.has(opt.value),
                          atMax,
                          onPick: () => togglePick(opt),
                          t,
                        }))}
                      </CommandGroup>
                    ))
                  ) : options.length > 0 ? (
                    <CommandGroup
                      heading={
                        canCreate
                          ? t('exclusions.picker.existingHeading', { defaultValue: 'From Shopify' })
                          : undefined
                      }
                    >
                      {options.map((opt) => renderOption(opt, {
                        showThumb: isProduct,
                        isSelected: selectedValues.has(opt.value),
                        atMax,
                        onPick: () => togglePick(opt),
                        t,
                      }))}
                    </CommandGroup>
                  ) : null}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selections.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selections.map((sel) => (
            <Badge key={sel.value} variant="secondary" className="gap-1 py-0.5 pl-2 pr-1">
              <span className="max-w-[220px] truncate">{sel.display_label}</span>
              <button
                type="button"
                onClick={() => removeSelection(sel.value)}
                className="rounded-sm p-0.5 opacity-60 transition-opacity hover:bg-muted hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label={t('exclusions.picker.removeAria', { label: sel.display_label })}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}

      {atMax ? (
        <p className="text-xs text-muted-foreground">
          {t('exclusions.picker.maxHit', { max: maxSelections })}
        </p>
      ) : null}
    </div>
  );
}

function renderOption(
  opt: PickerOption,
  args: {
    showThumb: boolean;
    isSelected: boolean;
    atMax: boolean;
    onPick: () => void;
    t: (k: string, opts?: Record<string, unknown>) => string;
  },
) {
  const { showThumb, isSelected, atMax, onPick } = args;
  const disabled = !isSelected && atMax;
  return (
    <CommandItem
      key={opt.value}
      value={opt.value}
      onSelect={onPick}
      disabled={disabled}
      className={cn('flex items-center gap-2', disabled && 'opacity-50')}
    >
      {showThumb ? <Thumbnail src={opt.imageUrl} alt={opt.display_label} /> : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm">{opt.display_label}</span>
        {opt.secondary ? (
          <span className="truncate text-xs text-muted-foreground">{opt.secondary}</span>
        ) : null}
      </div>
      {isSelected ? <Check className="h-4 w-4 shrink-0" /> : null}
    </CommandItem>
  );
}

function Thumbnail({ src, alt }: { src: string | null | undefined; alt: string }) {
  if (!src) {
    return (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-border/50 bg-muted text-muted-foreground">
        <ImageOff className="h-4 w-4" />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="h-10 w-10 shrink-0 rounded-sm border border-border/50 bg-muted object-cover"
      loading="lazy"
    />
  );
}

function placeholderFor(field: MatchField, t: (k: string) => string): string {
  switch (field) {
    case 'shopify_product_tag':
      return t('exclusions.picker.placeholderTag');
    case 'shopify_product_type':
      return t('exclusions.picker.placeholderType');
    case 'shopify_product_id':
      return t('exclusions.picker.placeholderProduct');
    case 'shopify_variant_id':
      return t('exclusions.picker.placeholderVariant');
  }
}

function searchPlaceholderFor(field: MatchField, t: (k: string) => string): string {
  switch (field) {
    case 'shopify_product_tag':
      return t('exclusions.picker.searchTag');
    case 'shopify_product_type':
      return t('exclusions.picker.searchType');
    case 'shopify_product_id':
      return t('exclusions.picker.searchProduct');
    case 'shopify_variant_id':
      return t('exclusions.picker.searchVariant');
  }
}
